// Client for /api/v1/admin/shares (admin video shares) and the chunked upload
// protocol it speaks: open a multipart upload, PUT part_size slices, complete.

export type SharedVideo = {
  id: number;
  token: string;
  title: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  created_at: string;
  expires_at: string | null;
  expired: boolean;
  share_url: string;
};

export type SharedGroup = {
  id: number;
  token: string;
  title: string;
  columns: number | null;
  video_ids: number[];
  created_at: string;
  expires_at: string | null;
  expired: boolean;
  share_url: string;
};

export type SharesIndex = {
  videos: SharedVideo[];
  groups: SharedGroup[];
  part_size: number;
  max_video_bytes: number;
  max_group_videos: number;
};

// Mirrors TTL_OPTIONS_SECONDS on the server; null = never expires. Labels
// come from t.videoShares.ttl[key].
export type TtlKey = 'oneHour' | 'oneDay' | 'days7' | 'days30' | 'days90' | 'never';
export const TTL_CHOICES: { key: TtlKey; seconds: number | null }[] = [
  { key: 'oneHour', seconds: 3600 },
  { key: 'oneDay', seconds: 86400 },
  { key: 'days7', seconds: 7 * 86400 },
  { key: 'days30', seconds: 30 * 86400 },
  { key: 'days90', seconds: 90 * 86400 },
  { key: 'never', seconds: null },
];
export const DEFAULT_TTL_SECONDS = 7 * 86400;

async function expectOk<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

const jsonInit = (method: string, body: unknown): RequestInit => ({
  method,
  credentials: 'include',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export async function fetchShares(): Promise<SharesIndex> {
  return expectOk(await fetch('/api/v1/admin/shares', { credentials: 'include' }));
}

export async function deleteSharedVideo(id: number): Promise<void> {
  await expectOk(await fetch(`/api/v1/admin/shares/videos/${id}`, { method: 'DELETE', credentials: 'include' }));
}

export async function deleteSharedGroup(id: number): Promise<void> {
  await expectOk(await fetch(`/api/v1/admin/shares/groups/${id}`, { method: 'DELETE', credentials: 'include' }));
}

export async function createSharedGroup(input: {
  title: string;
  video_ids: number[];
  columns: number | null;
  ttl_seconds: number | null;
}): Promise<SharedGroup> {
  const { group } = await expectOk<{ group: SharedGroup }>(await fetch('/api/v1/admin/shares/groups', jsonInit('POST', input)));
  return group;
}

export type UploadProgress = { sent: number; total: number };

// Uploads one file in part_size slices. Parts go one at a time so a slow
// link never has more than one slice in flight; the server forwards each to
// R2 as a multipart part and only writes the share row on complete.
export async function uploadSharedVideo(
  file: File,
  opts: { title: string; ttl_seconds: number | null; onProgress?: (p: UploadProgress) => void; signal?: AbortSignal }
): Promise<SharedVideo> {
  const open = await expectOk<{ upload_id: string; key: string; part_size: number }>(
    await fetch('/api/v1/admin/shares/uploads', jsonInit('POST', { filename: file.name, content_type: file.type, size_bytes: file.size }))
  );
  const { upload_id: uploadId, key, part_size: partSize } = open;
  const abort = () =>
    fetch(`/api/v1/admin/shares/uploads/${encodeURIComponent(uploadId)}?key=${encodeURIComponent(key)}`, {
      method: 'DELETE',
      credentials: 'include',
    }).catch(() => undefined);

  try {
    const parts: { part_number: number; etag: string }[] = [];
    const total = Math.max(1, Math.ceil(file.size / partSize));
    for (let n = 1; n <= total; n++) {
      const slice = file.slice((n - 1) * partSize, Math.min(file.size, n * partSize));
      const res = await fetch(
        `/api/v1/admin/shares/uploads/${encodeURIComponent(uploadId)}/parts/${n}?key=${encodeURIComponent(key)}`,
        { method: 'PUT', credentials: 'include', body: slice, signal: opts.signal, headers: { 'Content-Type': 'application/octet-stream' } }
      );
      const { etag } = await expectOk<{ etag: string }>(res);
      parts.push({ part_number: n, etag });
      opts.onProgress?.({ sent: Math.min(file.size, n * partSize), total: file.size });
    }
    const { video } = await expectOk<{ video: SharedVideo }>(
      await fetch(`/api/v1/admin/shares/uploads/${encodeURIComponent(uploadId)}/complete`, {
        ...jsonInit('POST', {
          key,
          parts,
          title: opts.title,
          filename: file.name,
          content_type: file.type,
          size_bytes: file.size,
          ttl_seconds: opts.ttl_seconds,
        }),
        signal: opts.signal,
      })
    );
    return video;
  } catch (err) {
    void abort();
    throw err;
  }
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v >= 100 ? v.toFixed(0) : v.toFixed(1)} ${units[i]}`;
}

// "in 6 days", "in 3 h", "expired", or "never", using the current locale's
// templates (t.videoShares.expiry).
export type ExpiryLabels = { never: string; expired: string; inMinutes: string; inHours: string; inDays: string };
export function describeExpiry(expiresAt: string | null, labels: ExpiryLabels, now = Date.now()): string {
  const fill = (tpl: string, n: number) => tpl.replace('{n}', String(n));
  if (!expiresAt) return labels.never;
  const ms = new Date(expiresAt).getTime() - now;
  if (ms <= 0) return labels.expired;
  const h = ms / 3_600_000;
  if (h < 1) return fill(labels.inMinutes, Math.max(1, Math.round(ms / 60_000)));
  if (h < 48) return fill(labels.inHours, Math.round(h));
  return fill(labels.inDays, Math.round(h / 24));
}
